// File: frontend/src/components/forms/ContactForm.js

import React, { useState } from 'react';
import apiService from '../../services/apiService';
import { getCurrentUserContext } from '../../utils/bootstrap';
import { API_CONFIG } from '../../config/api';
import { useLanguage } from '../../contexts/LanguageContext';
import TextInput from '../common/TextInput';
import EmailInput from '../common/EmailInput';
import { Send } from 'lucide-react';
import styles from './ContactForm.module.css';
import { useRef } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const ContactForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email_address: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const turnstileRef = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState(null);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleEmailChange = (value) => {
    setFormData(prev => ({ ...prev, email_address: value }));
    
    if (errors.email_address) {
      setErrors(prev => ({ ...prev, email_address: null }));
    }
  };

  const handleMessageChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, message: value }));
    
    if (errors.message) {
      setErrors(prev => ({ ...prev, message: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = t('contact_form.errors.first_name_required', 'First name is required');
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = t('contact_form.errors.last_name_required', 'Last name is required');
    }
    
    if (!formData.email_address.trim()) {
      newErrors.email_address = t('contact_form.errors.email_required', 'Email is required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email_address)) {
      newErrors.email_address = t('contact_form.errors.email_invalid', 'Please enter a valid email');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('contact_form.errors.message_required', 'Message is required');
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!turnstileToken) {
      setErrors({ submit: 'Security check not complete. Please wait a moment and try again.' });
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      const userContext = getCurrentUserContext();
      
      const response = await apiService.submitContactForm({
        enterprise_id: API_CONFIG.enterpriseId,
        user_id: userContext?.userId || null,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email_address: formData.email_address.trim().toLowerCase(),
        message: formData.message.trim(),
        language: navigator.language.toLowerCase() || 'en-us',
        user_agent: navigator.userAgent,
        turnstile_token: turnstileToken, 
      });
      
      if (response.success) {
        setSubmitSuccess(true);

        setTurnstileToken(null);
        
        setTimeout(() => {
          setFormData({
            first_name: '',
            last_name: '',
            email_address: '',
            message: ''
          });
          setSubmitSuccess(false);
        }, 5000);
      } else {
        setErrors({ 
          submit: response.error || t('contact_form.errors.submit_failed', 'Failed to submit form. Please try again.')
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setErrors({ 
        submit: t('contact_form.errors.submit_error', 'An error occurred. Please try again later.')
      });
      turnstileRef.current?.reset();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className={styles.successCard}>
        <div className={styles.successIcon}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#ECFDF5"/>
            <path d="M9 12L11 14L15 10" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <h3 className={styles.successTitle}>
          {t('contact_form.success.title', 'Message Sent!')}
        </h3>
        <p className={styles.successMessage}>
          {t('contact_form.success.message', "Thank you for contacting us. We'll get back to you within 24 hours.")}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.formFields}>
        <div className={styles.nameRow}>
          <TextInput
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            label={t('contact_form.fields.first_name', 'First Name')}
            placeholder={t('contact_form.placeholders.first_name', 'John')}
            required
            error={errors.first_name}
          />
          
          <TextInput
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            label={t('contact_form.fields.last_name', 'Last Name')}
            placeholder={t('contact_form.placeholders.last_name', 'Doe')}
            required
            error={errors.last_name}
          />
        </div>
        
        <EmailInput
          value={formData.email_address}
          onChange={handleEmailChange}
          label={t('contact_form.fields.email', 'Email')}
          placeholder={t('contact_form.placeholders.email', 'you@example.com')}
          required
          error={errors.email_address}
        />
        
        <div className={styles.textareaGroup}>
          <label className={styles.label}>
            {t('contact_form.fields.message', 'Message')}
            <span className={styles.required}>*</span>
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleMessageChange}
            className={`${styles.textarea} ${errors.message ? styles.hasError : ''}`}
            placeholder={t('contact_form.placeholders.message', 'How can we help you?')}
            rows={5}
          />
          {errors.message && (
            <div className={styles.errorMessage}>{errors.message}</div>
          )}
        </div>
      </div>
      
      {errors.submit && (
        <div className={styles.submitError}>{errors.submit}</div>
      )}

      <Turnstile
        ref={turnstileRef}
        siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
        onSuccess={(token) => setTurnstileToken(token)}
        onExpire={() => setTurnstileToken(null)}
        onError={() => setTurnstileToken(null)}
        options={{ theme: 'auto' }}
      />
      
      <button
        type="submit"
        disabled={isSubmitting}
        className={styles.submitButton}
      >
        {isSubmitting ? (
          <>
            <span className={styles.spinner}></span>
            {t('contact_form.button.sending', 'Sending...')}
          </>
        ) : (
          <>
            <Send size={20} strokeWidth={2.5} />
            {t('contact_form.button.send', 'Send Message')}
          </>
        )}
      </button>
    </form>
  );
};

export default ContactForm;